import { useState, useContext } from 'react';
import {
  Card,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import { AuthContext } from './../../AuthContext/AuthContext';
import GuestLoginModal from '../../GuestLoginModal';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from './../../Api/ApiUrls';
const formatPropertyId = (id) => {
  return `PR${String(id).padStart(4, '0')}`;
};

const getPriceLabel = (price) => {
  const num = Number(price);
  if (num >= 10000000) {
    return `${(num / 10000000).toFixed(2)} Cr`;
  } else if (num >= 100000) {
    return `${(num / 100000).toFixed(2)} L`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(2)} K`;
  }
  return num.toLocaleString('en-IN');
};

// Default images for different property types
const getDefaultImageByType = (propertyType) => {
  const typeImages = {
    'Plots': 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=80&h=80&fit=crop',
    'Land': 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=80&h=80&fit=crop',
    'Flat': 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=80&h=80&fit=crop',
    'House': 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=80&h=80&fit=crop',
    'Villa': 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=80&h=80&fit=crop',
    'Commercial Building': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=80&h=80&fit=crop',
    'Residential Building': 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=80&h=80&fit=crop'
  };
  return typeImages[propertyType] || 'https://via.placeholder.com/80x80?text=No+Image';
};

export default function PropertyCard({ 
  property, 
  onCardClick, 
  onCallClick, 
  onMapClick,
  images = [],
  showAlert,
  selected = false  
}) {
  const { isGuest } = useContext(AuthContext);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [showGuestModal, setShowGuestModal] = useState(false);
const navigate = useNavigate();
  const hasMultipleImages = images.length > 1;
  const currentImage = images[currentImageIndex] || { url: null };

  const getImageUrl = (url) => {
    if (!url) return getDefaultImageByType(property.property_type);
    return url;
  };

  const handleImageError = () => {
    setImageErrors(prev => ({ ...prev, [currentImageIndex]: true }));
  };

  const getCurrentImageUrl = () => {
    if (imageErrors[currentImageIndex] || !currentImage?.url) {
      return getDefaultImageByType(property.property_type);
    }
    return currentImage.url;
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleDotClick = (index, e) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  // ✅ Guest check helper
  const checkGuestAndExecute = (actionCallback) => {
    if (isGuest) {
      setShowGuestModal(true);
      return false;
    }
    return actionCallback();
  };

// Handle location click
const handleLocationClick = (e) => {
  e.stopPropagation();
  
  checkGuestAndExecute(() => {
    setIsLoadingLocation(true);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const openInMaps = (url) => {
      setIsLoadingLocation(false);
      window.location.href = url;
    };

    const fallbackUrl = isIOS
      ? `http://maps.apple.com/?q=${encodeURIComponent(property.location || '')}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.location || `${property.lat},${property.lng}`)}`;

    if (property.lat && property.lng && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          const mapsUrl = isIOS
            ? `http://maps.apple.com/?daddr=${property.lat},${property.lng}`
            : `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${property.lat},${property.lng}&travelmode=driving`;
          openInMaps(mapsUrl);
        },
        (error) => {
          console.error("Error getting user location:", error);
          openInMaps(fallbackUrl);
        },
        { timeout: 10000 }
      );
    } else {
      openInMaps(fallbackUrl);
    }
  });
};


const handleCallClick = (e) => {
  e.stopPropagation();
  
  // First check if user is guest
  if (isGuest) {
    setShowGuestModal(true);
    return;
  }
  
  // Check for phone number in multiple possible locations
  const phoneNumber =
                     property.bankContactDetails ;
  
  if (phoneNumber) {
    // Copy to clipboard
    navigator.clipboard.writeText(phoneNumber)
      .then(() => {
        if (showAlert) {
          showAlert('success', `Phone number copied: ${phoneNumber}`);
        } else {
          alert(`Phone number copied: ${phoneNumber}`);
        }
      })
      .catch((error) => {
        console.error('Failed to copy:', error);
        if (showAlert) {
          showAlert('failure', 'Failed to copy phone number');
        } else {
          alert('Failed to copy phone number');
        }
      });
  } else {
    // Try to fetch from user endpoint as fallback
    const userId = property.user_id || property.userId;
    if (userId) {
      axios.get(`${BASE_URL}/users/${userId}/`)
        .then(response => {
          const user = response.data;
          const userPhone = user?.mobile_no || user?.bankContactDetails || user?.phone;
          if (userPhone) {
            return navigator.clipboard.writeText(userPhone);
          } else {
            throw new Error('No phone number found');
          }
        })
        .then(() => {
          if (showAlert) {
            showAlert('success', 'Phone number copied successfully');
          }
        })
        .catch(error => {
          console.error('Error fetching phone number:', error);
          if (showAlert) {
            showAlert('failure', 'No contact information available');
          } else {
            alert('No contact information available');
          }
        });
    } else {
      if (showAlert) {
        showAlert('failure', 'No contact information available');
      } else {
        alert('No contact information available');
      }
    }
  }
};
const handleCardClick = (e) => {
  const isButtonClick = e.target.closest('button') || 
                        e.target.closest('svg') || 
                        e.target.closest('path');
  if (!isButtonClick) {
    if (isGuest) {
      setShowGuestModal(true);
      return;
    }
          localStorage.setItem('lastViewedPropertyId', property.id);

    navigate(`/Post_Bankcard_indetail/${property.id}`);
  }
};

  return (
    <>
  <Card
        onClick={handleCardClick}
        sx={{
          display: "flex",
          flexDirection: "row",
          width: '100%',
          height: 125,
          maxWidth: 290,
          cursor: 'pointer',
          borderRadius: 2,
          overflow: 'hidden',
          border: selected ? '2px solid #FFD600' : '2px solid transparent',
          transform: selected ? 'translateY(-2px)' : 'none',
          transition: 'transform 200ms, border-color 200ms',
          boxShadow: selected ? '0 4px 12px rgba(0,0,0,0.5)' : '0 2px 6px rgba(0,0,0,0.3)',
          bgcolor: '#fff',
          flexShrink: 0,
        }}
      >
        {/* IMAGE SECTION */}
        <Box
          sx={{
            width: 80,
            height: 115, 
            ml: 1,
            borderRadius: "10px",
            overflow: "hidden",
            position: "relative",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {images.length > 0 ? (
            <>
              <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                <img
                  src={getCurrentImageUrl()}
                  alt="Property"
                  onError={handleImageError}
                  style={{ 
                    width: "100%", 
                    height: "100%", 
                    objectFit: "cover",
                    objectPosition: "center"
                  }}
                />
                
                {hasMultipleImages && (
                  <IconButton
                    onClick={prevImage}
                    sx={{
                      position: "absolute",
                      left: 2,
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      width: 20,
                      height: 20,
                      minWidth: 20,
                      padding: 0,
                      '&:hover': {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      }
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
                
                {hasMultipleImages && (
                  <IconButton
                    onClick={nextImage}
                    sx={{
                      position: "absolute",
                      right: 2,
                      top: "50%",
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      width: 20,
                      height: 20,
                      minWidth: 20,
                      padding: 0,
                      '&:hover': {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      }
                    }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
              
              {/* DOTS INDICATOR */}
              {hasMultipleImages && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 0.5, 
                  gap: 0.5,
                  position: "absolute",
                  bottom: 4,
                  left: 0,
                  right: 0,
                }}>
                  {images.map((_, index) => (
                    <Box
                      key={index}
                      onClick={(e) => handleDotClick(index, e)}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: index === currentImageIndex ? '#1976d2' : '#ccc',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: index === currentImageIndex ? '#1976d2' : '#aaa',
                        }
                      }}
                    />
                  ))}
                </Box>
              )}
            </>
          ) : (
            /* NO IMAGE STATE */
            <Box sx={{ 
              width: "100%", 
              height: "100%", 
              display: "flex", 
              flexDirection: "column",
              alignItems: "center", 
              justifyContent: "center",
              backgroundColor: "#f5f5f5"
            }}>
              <NoPhotographyIcon sx={{ fontSize: 24, color: "#999", mb: 1 }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: "10px", 
                  color: "#666",
                  textAlign: "center",
                  px: 1
                }}
              >
                No Image Available
              </Typography>
            </Box>
          )}
        </Box>

        {/* CONTENT SECTION - Your original content preserved */}
        <Box sx={{ 
          flex: 1, 
          px: 1.2, 
          minWidth: 0,
          height: 120, 
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }}>
          {/* Property ID and Price in one row with space-between */}
          <Box sx={{
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            mb: 0.8,
          }}>
            <Typography
              fontSize="10px"
              sx={{
                fontWeight: 400,
                maxWidth: '75%',
                whiteSpace: 'normal', 
              }}
            >
              <b>Property ID:</b> {formatPropertyId(property?.id || property?.bankprop_id)}
            </Typography>

            <Typography
              fontSize="10px"
              sx={{
                backgroundColor: "#000000cb",
                color: "#fff",
                borderRadius: "50px",
                minWidth: "35px",
                padding: "2px 6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                whiteSpace: "nowrap"
              }}
            >
              ₹ {getPriceLabel(property.reserve_price || property.price)}
            </Typography>
          </Box>

          {/* Property details with margins */}
          <Box sx={{ 
            flex: 1, 
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}>
            <Box mb={0.3}>
              <Typography fontSize="10px" noWrap sx={{ lineHeight: 1.2 }}>
                <b>Property:</b> {property.propertyType || ""}
              </Typography>
            </Box>

            <Box mb={0.3}>
              <Typography fontSize="10px" noWrap sx={{ lineHeight: 1.2 }}>
                <b>City/Town:</b> {property.city_town || property.city || "-"}
              </Typography>
            </Box>
            
            <Box mb={0.3}>
              <Typography fontSize="10px" noWrap sx={{ lineHeight: 1.2 }}>
                <b>Location:</b> {property.area_town || property.locality || "-"}
              </Typography>
            </Box>
            
            <Box mb={0.2}>
              <Typography fontSize="10px" noWrap sx={{ lineHeight: 1.2 }}>
                <b>Bank:</b> {property.bankName || property.title || "-"}
              </Typography>
            </Box>
            
            <Box mb={0.3}>
              <Typography fontSize="10px" noWrap sx={{ lineHeight: 1.2 }}>
                <b>Auction Date:</b>{" "}
                {property.auctionEnd ? (
                  `${new Date(property.auctionStart).toLocaleDateString("en-IN")} – ${new Date(property.auctionEnd).toLocaleDateString("en-IN")}`
                ) : (
                  "-"
                )}
              </Typography>
            </Box>
          </Box>

          {/* Buttons at the bottom */}
          <Box sx={{ 
            display: "flex", 
            justifyContent: 'space-evenly', 
            gap: 0.5
          }}>
            <Button 
              size="small" 
              variant="outlined" 
              startIcon={<CallIcon />}
              onClick={handleCallClick}
              sx={{ 
                minWidth: '58px',
                fontSize: '10px',
                padding: '1px 3px',
                height: '22px',
                flex: 1
              }}
            >
              Call
            </Button>

            <Button 
              size="small" 
              variant="outlined" 
              startIcon={!isLoadingLocation && <LocationOnIcon />}
              onClick={handleLocationClick}
              disabled={isLoadingLocation}
              sx={{ 
                minWidth: '58px',
                fontSize: '10px',
                padding: '1px 3px',
                height: '22px',
                flex: 1
              }}
            >
              {isLoadingLocation ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                "Map"
              )}
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Guest Login Modal */}
      <GuestLoginModal
        open={showGuestModal}
        onClose={() => setShowGuestModal(false)}
      />
    </>
  );
}